import React, { useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import PageLayout from '../../components/layout/PageLayout';
import { authApi } from '../../api/auth';
import { acceptLogin } from './session';
import { AccountBoundary, useSettings, useRequestScope } from '../settings/shared';
import { useExpandedText } from '../notifications/useExpandedText';
import { Field } from '../password/PasswordPage';
import { translate } from '../password/model';
import baseline from '../../app-ui/baseline';
import { SocialProviderLogo } from './SocialProviderLogo';
import { authReturnPath, socialUrl, loginError, validateLoginUser } from './model';
import {
  appleWebEnabled,
  beginAppleFlow,
  clearAppleFlow,
  appleFlowCurrent,
  appleAuthorizationUrl,
} from './appleWeb';
import { navigateToApple } from './appleNavigation';
import './Auth.css';
export default function LoginPage() {
  const location = useLocation();
  return <AccountBoundary key={location.key} component={LoginForm} />;
}
function LoginForm({ user }) {
  const { style, locale, dark } = useSettings(),
    expanded = useExpandedText(),
    location = useLocation(),
    nav = useNavigate();
  const target = authReturnPath(new URLSearchParams(location.search).get('redirectUrl'));
  const t = (k, v) => translate(locale, k, v),
    request = useRequestScope(user);
  const [email, setEmail] = useState(''),
    [password, setPassword] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(null);
  const lock = useRef(false);
  const login = async (event) => {
    event.preventDefault();
    if (lock.current) return;
    if (!email.trim() || !password) {
      setError(t('auth.emailPasswordRequired'));
      return;
    }
    lock.current = true;
    setBusy('email');
    setError('');
    const r = request();
    try {
      const response = await authApi.login(
        { email: email.trim(), password, rememberMe: true },
        r.options,
      );
      if (!r.current()) return;
      let accepted;
      try {
        accepted = validateLoginUser(response?.data, t);
      } catch (e) {
        setError(e.message);
        return;
      }
      // Commit only the successful current attempt; never write a candidate token before validation.
      setPassword('');
      setEmail('');
      acceptLogin(accepted);
      nav(target, { replace: true });
    } catch (e) {
      if (r.current()) setError(loginError(e, t('auth.loginFailed')));
    } finally {
      if (r.current()) {
        lock.current = false;
        setBusy(null);
      }
    }
  };
  const social = async (provider) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(provider);
    setError('');
    const r = request();
    let appleFlow;
    let departing = false;
    try {
      if (provider === 'apple') {
        appleFlow = beginAppleFlow(target);
        const response = await authApi.startAppleWeb(
          { tabKey: appleFlow.tabKey, returnPath: target },
          r.options,
        );
        if (!r.current() || !appleFlowCurrent(appleFlow)) return;
        const url = appleAuthorizationUrl(response?.data?.url);
        if (!url) throw new Error('Invalid Apple destination');
        navigateToApple(url);
        departing = true;
        return;
      }
      const method = { google: 'getGoogleUrl', naver: 'getNaverUrl', kakao: 'getKakaoUrl' }[
        provider
      ];
      const response = await authApi[method](target, r.options);
      if (!r.current()) return;
      const url = socialUrl(response?.data?.url);
      if (!url) {
        setError(t('auth.socialLoginFailed'));
        return;
      }
      window.location.assign(url);
    } catch (e) {
      if (r.current())
        setError(
          provider === 'apple'
            ? t('auth.socialLoginFailed')
            : loginError(e, t('auth.socialLoginFailed')),
        );
    } finally {
      if (appleFlow && !departing) clearAppleFlow(appleFlow);
      if (r.current()) {
        lock.current = false;
        setBusy(null);
      }
    }
  };
  if (user) return <Navigate to={target} replace />;
  const colors = baseline.tokens[dark ? 'darkColors' : 'lightColors'];
  const signup = '/signup?redirectUrl=' + encodeURIComponent(target);
  return (
    <PageLayout paddingTop={0} paddingBottom={0}>
      <div
        className={'pp-settings pp-password pp-auth' + (expanded ? ' is-expanded' : '')}
        style={{ ...style, '--password-surface': colors.surface }}
      >
        <header className="pp-settings-header">
          <button
            type="button"
            disabled={!!busy}
            aria-label={t('common.back')}
            onClick={() =>
              window.history.state?.idx > 0 ? nav(-1) : nav(target, { replace: true })
            }
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{t('auth.login')}</h1>
        </header>
        <p className="pp-auth-notice">{t('auth.returnAfterLogin')}</p>
        <form aria-label={t('auth.login')} noValidate onSubmit={login}>
          <fieldset disabled={!!busy}>
            <Field
              id="login-email"
              label={t('auth.email')}
              value={email}
              onChange={setEmail}
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
              required
              t={t}
            />
            <Field
              id="login-password"
              label={t('auth.password')}
              value={password}
              onChange={setPassword}
              placeholder={t('auth.passwordPlaceholder')}
              autoComplete="current-password"
              required
              secret
              toggle
              t={t}
            />
            {error && (
              <p className="pp-password-error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="pp-password-submit" aria-busy={busy === 'email'}>
              {busy === 'email' ? t('auth.loggingIn') : t('auth.login')}
            </button>
          </fieldset>
        </form>
        <div className="pp-auth-links">
          <Link to={signup} aria-disabled={!!busy} onClick={(e) => busy && e.preventDefault()}>
            {t('auth.signup')}
          </Link>
          <span aria-hidden="true" />
          <Link
            to="/password-forgot"
            aria-disabled={!!busy}
            onClick={(e) => busy && e.preventDefault()}
          >
            {t('auth.findPassword')}
          </Link>
        </div>
        <section className="pp-auth-social" aria-label={t('auth.quickLogin')}>
          <div className="pp-auth-divider">
            <span />
            {t('auth.quickLogin')}
            <span />
          </div>
          <div className="pp-auth-providers">
            {(appleWebEnabled()
              ? ['apple', 'google', 'naver', 'kakao']
              : ['google', 'naver', 'kakao']
            ).map((provider) => (
              <button
                key={provider}
                type="button"
                disabled={!!busy}
                aria-busy={busy === provider}
                aria-label={t('auth.continueWith', {
                  provider: { apple: 'Apple', google: 'Google', naver: 'Naver', kakao: 'Kakao' }[
                    provider
                  ],
                })}
                onClick={() => social(provider)}
              >
                {busy === provider ? (
                  <span className="pp-auth-spinner" aria-hidden="true" />
                ) : (
                  <SocialProviderLogo provider={provider} isDark={dark} />
                )}
              </button>
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
